import { Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

// Reusable heart button component for favorites
export default function HeartButton({ 
  isFavorite, 
  onToggle, 
  size = "md",
  className = "",
  bare = false,
  showTooltip = true,
  freeAllowed = false,
  disabled = false,
  label,
  testId
}) {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  
  const hasAccess = isAuthenticated && user?.subscription_status === "active";

  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6"
  };

  const buttonSizeClasses = {
    sm: "p-1.5",
    md: "p-2",
    lg: "p-2.5"
  };

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check auth first
    if (!isAuthenticated) {
      toast("Sign in to save favorites", {
        duration: 5000,
        action: { label: "Sign In", onClick: () => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`) },
      });
      return;
    }

    // Check membership
    if (!hasAccess && !freeAllowed) {
      toast("Become a member to save favorites", {
        duration: 5000,
        action: { label: "Subscribe", onClick: () => navigate("/subscribe") },
      });
      return;
    }

    // User is authenticated and has access - toggle favorite
    if (onToggle) {
      await onToggle();
    }
  };

  return (
    <button
      type="button"
      onClick={disabled ? undefined : handleClick}
      disabled={disabled}
      aria-pressed={!!isFavorite}
      data-testid={testId}
      className={bare ? `${className} bg-transparent rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B6A46D]` : `
        ${buttonSizeClasses[size]} 
        bg-white/90 rounded-full hover:bg-white 
        transition-all duration-200 
        hover:scale-110 active:scale-95
        shadow-sm hover:shadow
        ${className}
      `}
      title={label || (isFavorite ? "Remove from favorites" : "Add to favorites")}
      aria-label={label || (isFavorite ? "Remove from favorites" : "Add to favorites")}
    >
      <Heart
        className={`
          ${sizeClasses[size]} 
          transition-colors duration-200
        `}
        style={{ 
          color: isFavorite ? '#e53e3e' : '#0f1d2f',
        }}
        fill={isFavorite ? '#e53e3e' : 'none'}
        strokeWidth={isFavorite ? 2 : 1.5}
      />
    </button>
  );
}
